import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
} from '@mui/material';
import {
  Check as CheckIcon,
} from '@mui/icons-material';

/**
 * Language Switcher Component
 * 
 * Allows users to switch between Portuguese and English.
 * 
 * Features:
 * - Dropdown menu with available languages
 * - Visual indicator for current language
 * - Persists language preference to localStorage
 * - Updates Accept-Language header for API calls
 * - Flag icons for visual language identification
 * 
 * @author People Rock Development Team
 * @version 1.0
 * @since Phase 3
 */

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇵🇹',
  },
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇬🇧',
  },
];

export const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Get current language, fallback to Portuguese if not found
  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0]!;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: string) => {
    await i18n.changeLanguage(languageCode);
    
    // Update HTML lang attribute
    document.documentElement.lang = languageCode;
    
    // Store preference in localStorage (i18next does this automatically, but we ensure it)
    try {
      localStorage.setItem('i18nextLng', languageCode);
    } catch {
      // storage unavailable (private mode): the choice just lasts for this visit
    }
    
    handleClose();
  };

  return (
    <Box>
      <Tooltip title={`${currentLanguage.nativeName} (${currentLanguage.name})`}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          aria-label={t('a11y.selectLanguage', 'Select language')}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '1.5rem',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {currentLanguage.flag}
          </Box>
        </IconButton>
      </Tooltip>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-label': 'language',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => {
          const isSelected = language.code === i18n.language;

          return (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              selected={isSelected}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: '1.5rem',
                    lineHeight: 1,
                  }}
                >
                  {language.flag}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={language.nativeName}
                secondary={language.name}
                primaryTypographyProps={{
                  fontWeight: isSelected ? 600 : 400,
                }}
              />
              {isSelected && (
                <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher;
